import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import { PaginationFooter } from '@/components/synapse/PaginationFooter';
import { TypePill } from '@/components/synapse/TypePill';
import { Badge } from '@/components/ui/badge';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { isKnownItemType, translateUnit } from '@/lib/itemTypeConfig';
import type { ItemType } from '@/lib/itemTypeConfig';
import type { IssueLogRow } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useIssueLog } from './useIssuingAnalytics';
import { calendarLabel, timestampLabel } from './issuingWindow';

const PAGE_SIZE = 25;

interface IssueLogPanelProps {
  from: string;
  to: string;
  itemTypes: readonly ItemType[];
  itemIds: readonly string[];
}

/**
 * The lines behind the numbers.
 *
 * Paged separately from the panels above and deliberately de-emphasised: it is
 * the drill-down an admin opens to check a figure, not the reason they came. It
 * inherits the control bar's window and filters exactly, so what it lists is
 * always an honest expansion of what the charts are showing.
 */
export function IssueLogPanel({ from, to, itemTypes, itemIds }: IssueLogPanelProps) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);

  // Any change to what is being counted invalidates the page number: page 4 of a
  // now-one-page result is an offset past the end, i.e. an empty table under a
  // filter that does have rows.
  //
  // Reset during render rather than in an effect, because the request below is
  // subscribed during render too. An effect runs after that — so a filter change
  // on page 3 would fetch offset 50 under the new filter, paint those rows, and
  // only then reset and fetch again: one wasted round trip and a flash of the
  // wrong page.
  const filterKey = [from, to, itemTypes.join(','), itemIds.join(',')].join('|');
  const [pagedFilterKey, setPagedFilterKey] = useState(filterKey);
  if (filterKey !== pagedFilterKey) {
    setPagedFilterKey(filterKey);
    setPage(1);
  }
  // What `page` will be once the reset above lands, which is what the rest of
  // this render has to use — a page number belonging to the previous filter is
  // never the right offset to ask for.
  const currentPage = filterKey === pagedFilterKey ? page : 1;

  const { data, isLoading, isError, error, refetch, isFetching } = useIssueLog({
    from,
    to,
    itemTypes,
    itemIds,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, currentPage * PAGE_SIZE);

  return (
    <section aria-label={t('analytics.log.title')}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
          {t('analytics.log.title')}
        </h2>
        <span className="text-[11.5px] text-muted-2">{t('analytics.log.subtitle')}</span>
      </div>

      {isLoading ? (
        <Card className="overflow-hidden p-0" aria-busy="true" aria-live="polite">
          <ul className="divide-y divide-line">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="h-[62px] animate-pulse bg-paper-2" />
            ))}
          </ul>
        </Card>
      ) : isError ? (
        <Card className="flex flex-col items-center gap-2 py-8 text-center">
          <Icon name="alert" size={20} className="text-destructive" />
          <div className="text-[13.5px] font-medium">{t('analytics.log.couldNotLoad')}</div>
          <p className="text-[12.5px] text-muted">
            {error instanceof ApiError ? translateApiError(error) : t('errors.fallback')}
          </p>
          <Button size="sm" onClick={() => refetch()}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      ) : items.length === 0 ? (
        <Card className="py-8 text-center text-[12.5px] text-muted">
          {t('analytics.log.empty')}
        </Card>
      ) : (
        <Card className={cn('overflow-hidden p-0', isFetching && 'opacity-95')}>
          <ul className="divide-y divide-line">
            {items.map((row) => (
              <li key={row.movementId}>
                <LogRow row={row} lang={i18n.language} />
              </li>
            ))}
          </ul>
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Card>
      )}
    </section>
  );
}

/**
 * One ledger line. The leading timestamp column only exists once the row is wide
 * enough to align it; below that it joins the metadata line, so the row degrades
 * into a card without a second copy of the markup.
 *
 * Both dates go through `lang`. The row carries two of them, and formatting one
 * in the app's language and the other in the browser's put a Sinhala expiry next
 * to an English timestamp on the same line.
 */
function LogRow({ row, lang }: { row: IssueLogRow; lang: string }) {
  const { t } = useTranslation();
  const whenText = timestampLabel(row.issuedAt, lang);
  const secondary = [row.itemGenericName, row.itemStrength].filter(Boolean).join(' · ');

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="hidden w-[132px] shrink-0 pt-0.5 text-[11.5px] tabular-nums text-muted md:block">
        {whenText}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {isKnownItemType(row.itemType) ? (
            <TypePill type={row.itemType} size="sm" />
          ) : (
            <Badge tone="neutral" size="sm">
              {row.itemType}
            </Badge>
          )}
          <span className="truncate text-[13.5px] font-semibold tracking-[-0.01em]">
            {row.itemName}
          </span>
          {secondary && <span className="truncate text-[12px] text-muted">{secondary}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-muted">
          <span className="md:hidden">{whenText}</span>
          <span>
            {row.batchNumber ? `· ${row.batchNumber}` : `· ${t('analytics.log.noBatch')}`}
          </span>
          <span>
            ·{' '}
            {row.expiryDate
              ? t('analytics.log.expires', { date: calendarLabel(row.expiryDate, lang) })
              : t('analytics.log.noExpiry')}
          </span>
          <span>· {row.actorName}</span>
        </div>
      </div>

      <span className="shrink-0 text-right">
        <span className="font-mono text-[14px] font-semibold tabular-nums text-ink">
          {row.quantity}
        </span>
        <span className="ml-1 text-[11px] text-muted">{translateUnit(t, row.unit)}</span>
      </span>
    </div>
  );
}
